import React, { useState, useEffect, useCallback } from "react";
import { Image, AlertCircle } from "lucide-react";
import { supabase } from "../lib/supabase";
import { SubitemsService } from "../services/SubitemsService";
import LoadingSpinner from "./LoadingSpinner";
import { Modal, ModalBody, ModalFooter, Button } from "./ui";
import SubitemStatusIndicator from "./SubitemStatusIndicator";
import "./BIsModal.css";

// Phase order for sorting
const PHASE_ORDER = [
  "01-Data Prep",
  "02-Utilities",
  "03-Discrepancy Check",
  "04-Erosion/Pond",
  "05-Modeling",
  "06-Double Check",
  "07-Corrections",
  "08-Review",
  "09-Ready to Export",
  "10-Completed",
];

// Stage colors configuration matching the project phases
const STAGE_COLORS = {
  "01-Data Prep": { bg: "#f59e0b", text: "#ffffff" }, // Amber
  "02-Utilities": { bg: "#10b981", text: "#ffffff" }, // Emerald
  "03-Discrepancy Check": { bg: "#ef4444", text: "#ffffff" }, // Red
  "04-Erosion/Pond": { bg: "#06b6d4", text: "#ffffff" }, // Cyan
  "05-Modeling": { bg: "#8b5cf6", text: "#ffffff" }, // Violet
  "06-Double Check": { bg: "#f97316", text: "#ffffff" }, // Orange
  "07-Corrections": { bg: "#ec4899", text: "#ffffff" }, // Pink
  "08-Review": { bg: "#6366f1", text: "#ffffff" }, // Indigo
  "09-Ready to Export": { bg: "#10b981", text: "#ffffff" }, // Emerald
  "10-Completed": { bg: "#6b7280", text: "#ffffff" }, // Gray
};

// Map modeling status to phase
const STATUS_TO_PHASE_MAP = {
  "Data Prep": "01-Data Prep",
  Utilities: "02-Utilities",
  Modeling: "05-Modeling",
  "Double Check": "06-Double Check",
  Corrections: "07-Corrections",
  Review: "08-Review",
  "Ready to Export": "09-Ready to Export",
  Completed: "10-Completed",
};

const BIsModal = ({ isOpen, onClose }) => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updatingItems, setUpdatingItems] = useState(new Set());
  const [successMessage, setSuccessMessage] = useState(null);
  const [projectsData, setProjectsData] = useState(new Map()); // Track project data for real-time updates

  // Fetch projects with background image export subitems
  // Only include active projects with completed georeference tasks
  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Background image export task names (base and revision)
      const biTaskNames = [
        "Export Background Images",
        "Export Revised Background Images",
        "Export Revised Background\n  Images", // Handle legacy name with newline
      ];

      // Georeference task names (old and new naming conventions)
      const geoTaskNames = [
        "Georeference All Plan Sheets",
        "Import CAD and Georeference PDFs",
        "Georeference Revised PDFs",
      ];

      // First, get all projects with background image export subitems
      const { data: biSubitems, error: biError } = await supabase
        .from("subitems")
        .select(
          `
          id,
          name,
          subitem_status,
          project_id,
          projects!fk_project (
            id,
            name,
            job_number,
            rev,
            modeling_state,
            modeling_status
          )
        `,
        )
        .in("name", biTaskNames);

      if (biError) {
        throw biError;
      }

      // Get all projects that have completed any georeference task
      const { data: geoSubitems, error: geoError } = await supabase
        .from("subitems")
        .select("project_id")
        .in("name", geoTaskNames)
        .eq("subitem_status", "Done");

      if (geoError) {
        throw geoError;
      }

      // Create a set of project IDs that have completed georeference
      const projectsWithCompletedGeo = new Set(
        geoSubitems.map((item) => item.project_id),
      );

      // Filter to only include active projects with completed georeference AND incomplete BIs
      const filteredData = (biSubitems || [])
        .filter((item) => {
          // Check if project is active (not on hold or archived)
          const isActive = item.projects?.modeling_state === "Active";

          // Exclude completed projects (modeling_status = "Completed")
          const isNotCompleted = item.projects?.modeling_status !== "Completed";

          // Check if project has completed georeference
          const hasCompletedGeo = projectsWithCompletedGeo.has(item.project_id);

          // Check if BI is NOT completed (only 'Done' means complete)
          const biIncomplete =
            !item.subitem_status || item.subitem_status !== "Done";

          return isActive && isNotCompleted && hasCompletedGeo && biIncomplete;
        })
        .map((item) => {
          const projectData = {
            subitemId: item.id,
            status: item.subitem_status || '', // Keep the actual status
            projectId: item.projects?.id || item.project_id,
            projectName: item.projects?.name || "Unknown Project",
            jobNumber: item.projects?.job_number,
            rev: item.projects?.rev,
            currentPhase:
              STATUS_TO_PHASE_MAP[item.projects?.modeling_status] || null,
          };
          
          // Store project data for real-time updates
          setProjectsData(prev => {
            const newMap = new Map(prev);
            newMap.set(item.projects?.id || item.project_id, {
              name: item.projects?.name || "Unknown Project",
              jobNumber: item.projects?.job_number,
              rev: item.projects?.rev,
              modelingStatus: item.projects?.modeling_status,
            });
            return newMap;
          });
          
          return projectData;
        });

      // Sort by phase order (matching the stage sorting in other parts of the app)
      filteredData.sort((a, b) => {
        // Get phase indices for sorting
        const phaseIndexA = a.currentPhase
          ? PHASE_ORDER.indexOf(a.currentPhase)
          : 999;
        const phaseIndexB = b.currentPhase
          ? PHASE_ORDER.indexOf(b.currentPhase)
          : 999;

        // First sort by phase order
        if (phaseIndexA !== phaseIndexB) {
          return phaseIndexA - phaseIndexB;
        }

        // If same phase, sort by project name
        return a.projectName.localeCompare(b.projectName);
      });

      setProjects(filteredData);

      console.log(
        `Found ${filteredData.length} active projects with completed georeference and incomplete BIs`,
      );
    } catch (err) {
      console.error("Error fetching background images data:", err);
      setError("Failed to load background images data");
    } finally {
      setLoading(false);
    }
  }, []);

  // Background image export task names for real-time filtering
  const biTaskNamesForRealtime = [
    "Export Background Images",
    "Export Revised Background Images",
    "Export Revised Background\n  Images",
  ];

  // Handle real-time subitem updates
  const handleRealtimeSubitemUpdate = useCallback((updatedSubitem) => {
    console.log('🔴 BIsModal: Received real-time update:', updatedSubitem);

    // Only process background image export subitems
    if (!biTaskNamesForRealtime.includes(updatedSubitem.name)) {
      return;
    }
    
    // If this subitem was just marked as "Done", remove it from the list
    if (updatedSubitem.subitemStatus === "Done") {
      console.log('🔴 BIsModal: Removing completed BI subitem:', updatedSubitem.id);
      setProjects(prev => prev.filter(p => p.subitemId !== updatedSubitem.id));
      
      // Show success message
      const projectData = projectsData.get(updatedSubitem.projectId);
      if (projectData) {
        setSuccessMessage(`✅ ${projectData.name} marked complete`);
        setTimeout(() => setSuccessMessage(null), 3000);
      }
    } else {
      // If status changed to incomplete, we might need to re-add it
      // For now, just refresh the list to be safe
      console.log('🔴 BIsModal: BI subitem status changed, refreshing list');
      fetchProjects();
    }
  }, [projectsData, fetchProjects]);

  // Set up real-time subscription for "Export Background Images" subitems
  // We'll use a custom effect since we need to listen to ALL projects, not just one
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    console.log('🔴 BIsModal: Setting up real-time subscription for background image exports');

    // Subscribe to changes on subitems table - filter in handler since we have multiple task names
    const channel = supabase
      .channel('bis-modal-subitems')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'subitems',
        },
        (payload) => {
          console.log('🔴 BIsModal: Real-time update received:', payload);
          
          if (payload.eventType === 'UPDATE' && payload.new) {
            const updatedSubitem = {
              id: payload.new.id,
              name: payload.new.name,
              subitemStatus: payload.new.subitem_status,
              projectId: payload.new.project_id
            };
            
            handleRealtimeSubitemUpdate(updatedSubitem);
          }
        }
      )
      .subscribe((status) => {
        console.log('🔴 BIsModal: Subscription status:', status);
      });

    // Cleanup subscription
    return () => {
      console.log('🔴 BIsModal: Cleaning up real-time subscription');
      supabase.removeChannel(channel);
    };
  }, [isOpen, handleRealtimeSubitemUpdate]);

  // Load data when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchProjects();
    }
  }, [isOpen, fetchProjects]);


  // Handle status change using standardized service
  const handleStatusChange = async (subitemId, newStatus) => {
    // Add to updating set
    setUpdatingItems((prev) => new Set(prev).add(subitemId));

    try {
      // Use the same service as SubitemsTable for consistency
      const updatedSubitem = await SubitemsService.updateSubitem(subitemId, {
        subitemStatus: newStatus
      });

      console.log('✅ BIsModal: Successfully updated subitem:', updatedSubitem);

      const project = projects.find((p) => p.subitemId === subitemId);

      // Update the project status in the local state
      setProjects((prev) =>
        prev.map((p) =>
          p.subitemId === subitemId ? { ...p, status: newStatus } : p
        )
      );

      // Show success message
      if (newStatus === 'Done') {
        setSuccessMessage(`✅ ${project?.projectName} marked complete`);
        // Remove item from list after successful update (with slight delay for UX)
        setTimeout(() => {
          setProjects((prev) => prev.filter((p) => p.subitemId !== subitemId));
        }, 1500);
      } else if (newStatus) {
        setSuccessMessage(`${project?.projectName} status: ${newStatus}`);
      } else {
        setSuccessMessage(`${project?.projectName} status cleared`);
      }
      setTimeout(() => setSuccessMessage(null), 3000);

    } catch (err) {
      console.error("Error updating subitem status:", err);
      setError(`Failed to update status: ${err.message || 'Unknown error'}`);
      setTimeout(() => setError(null), 5000);
    } finally {
      // Remove from updating set
      setUpdatingItems((prev) => {
        const newSet = new Set(prev);
        newSet.delete(subitemId);
        return newSet;
      });
    }
  };

  // Format project display name
  const formatProjectDisplay = (project) => {
    let display = project.projectName;
    if (project.jobNumber) {
      display += ` (${project.jobNumber}`;
      if (project.rev && parseInt(project.rev) > 0) {
        display += ` Rev${project.rev}`;
      }
      display += ")";
    }
    return display;
  };

  if (!isOpen) return null;

  return (
    <Modal trackAs="BIsModal" isOpen={isOpen} onClose={onClose} size="lg">
      {/* Custom Header with Icon and Title */}
      <div
        style={{
          padding: 'var(--space-6)',
          borderBottom: '1px solid var(--gray-700)',
          background: 'linear-gradient(to bottom, var(--dark-bg), var(--dark-surface))',
          borderTopLeftRadius: 'var(--radius-xl)',
          borderTopRightRadius: 'var(--radius-xl)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
          <div
            style={{
              width: '56px',
              height: '56px',
              borderRadius: 'var(--radius-full)',
              backgroundColor: 'var(--primary-cyan)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Image size={28} color="white" />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1
              style={{
                margin: 0,
                fontSize: 'var(--text-2xl)',
                fontWeight: 'var(--font-semibold)',
                color: 'var(--gray-50)',
              }}
            >
              Background Images Status
            </h1>
            <p
              style={{
                margin: 0,
                marginTop: 'var(--space-1)',
                fontSize: 'var(--text-sm)',
                color: 'var(--gray-400)',
              }}
            >
              Track completion of background image exports
            </p>
          </div>
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="bis-success-message">{successMessage}</div>
      )}

      <ModalBody>
          {loading ? (
            <div className="bis-loading">
              <LoadingSpinner message="Loading background images data..." />
            </div>
          ) : error ? (
            <div className="bis-error">
              <AlertCircle size={24} />
              <p>{error}</p>
              <button onClick={fetchProjects} className="bis-retry-btn">
                Retry
              </button>
            </div>
          ) : projects.length === 0 ? (
            <div className="bis-empty">
              <Image size={48} className="bis-empty-icon" />
              <p>No projects need background images exported.</p>
              <p className="bis-empty-detail">
                All active projects with completed georeferencing have their
                background images marked as done.
              </p>
            </div>
          ) : (
            <div className="bis-table">
              <div className="bis-table-header">
                <div className="bis-header-project">Project Name</div>
                <div className="bis-header-status">Status</div>
              </div>
              <div className="bis-table-body">
                {projects.map((project) => (
                  <div
                    key={project.subitemId}
                    className="bis-row"
                  >
                    <div className="bis-project-name">
                      {project.currentPhase && (
                        <span
                          className="bis-stage-badge"
                          style={{
                            backgroundColor:
                              STAGE_COLORS[project.currentPhase]?.bg ||
                              "#6b7280",
                            color:
                              STAGE_COLORS[project.currentPhase]?.text ||
                              "#ffffff",
                          }}
                        >
                          {project.currentPhase
                            .replace(/^\d+-/, "")
                            .toUpperCase()}
                        </span>
                      )}
                      <span className="bis-project-text">
                        {formatProjectDisplay(project)}
                      </span>
                    </div>
                    <div className="bis-status">
                      <SubitemStatusIndicator
                        status={project.status}
                        onStatusChange={(newStatus) => handleStatusChange(project.subitemId, newStatus)}
                        isUpdating={updatingItems.has(project.subitemId)}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
      </ModalBody>

      <ModalFooter>
        <div
          style={{
            flex: 1,
            fontSize: 'var(--text-sm)',
            color: 'var(--gray-400)',
          }}
        >
          {!loading &&
            (projects.length > 0 ? (
              <>
                {projects.length} project{projects.length !== 1 ? "s" : ""}{" "}
                need{projects.length === 1 ? "s" : ""} background images
              </>
            ) : (
              <>✅ All background images completed!</>
            ))}
        </div>
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default BIsModal;
